import React from 'react';
import { AstrolabeData, Palace } from '../types';

interface AstrolabeViewProps {
  data: AstrolabeData;
}

const PalaceCard: React.FC<{ palace: Palace; isCenter?: boolean; info?: any }> = ({ palace, isCenter, info }) => {
  if (isCenter) {
    return (
      <div className="col-span-2 row-span-2 bg-slate-900/80 border-2 border-amber-600/50 rounded-lg p-4 flex flex-col items-center justify-center text-center backdrop-blur-sm">
        <h2 className="text-2xl font-bold text-amber-500 mb-2 font-serif">紫微斗數</h2>
        <div className="text-sm text-slate-300 space-y-1">
            <p>農曆: {info.chineseDate}</p>
            <p>{info.fiveElementsClass}</p>
        </div>
        <div className="mt-4 w-12 h-12 border-2 border-amber-500 rounded-full flex items-center justify-center">
            <span className="text-2xl">☯</span>
        </div>
      </div>
    );
  }

  // Determine border colors based on importance (Life palace gets Gold)
  const isLifePalace = palace.name === '命宮';
  const borderColor = isLifePalace ? 'border-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.2)]' : 'border-slate-700 hover:border-slate-500';

  return (
    <div className={`relative p-2 h-32 md:h-40 border ${borderColor} bg-slate-800/50 rounded flex flex-col justify-between transition-all`}>
        {/* Header: Palace Name & Stem/Branch */}
        <div className="flex justify-between items-start">
            <span className={`text-xs md:text-sm font-bold ${isLifePalace ? 'text-amber-400' : 'text-slate-400'}`}>
                {palace.heavenlyStem}{palace.earthlyBranch}
            </span>
            <span className={`writing-vertical-rl text-sm md:text-base font-bold tracking-widest ${isLifePalace ? 'text-white bg-amber-600 px-1 rounded-sm' : 'text-slate-300'}`}>
                {palace.name}
            </span>
        </div>

        {/* Stars */}
        <div className="flex-1 flex flex-col justify-center items-start pl-1">
             <div className="flex flex-wrap gap-1">
                {palace.majorStars.map((star, i) => (
                    <span key={i} className="text-red-400 font-bold text-xs md:text-sm">
                        {star.name}<span className="text-[10px] text-slate-500 ml-px opacity-70">{star.brightness}</span>
                    </span>
                ))}
             </div>
             <div className="flex flex-wrap gap-1 mt-1">
                {palace.minorStars.slice(0, 4).map((star, i) => (
                    <span key={i} className="text-blue-300 text-[10px] md:text-xs">
                        {star.name}
                    </span>
                ))}
             </div>
        </div>
    </div>
  );
};

const AstrolabeView: React.FC<AstrolabeViewProps> = ({ data }) => {
    // Ziwei charts are traditionally 4x4 grids.
    // The indices in iztro usually follow 0 = Life Palace, but physically on chart:
    // It depends on the Earthly Branch (Si, Wu, Wei...) positions which are fixed.
    // 
    // Standard Layout (Sip-Si Map):
    // Si(SE)  Wu(S)   Wei(SW)  Shen(SW)
    // Chen(E)                  You(W)
    // Mao(E)                   Xu(NW)
    // Yin(NE) Chou(N) Zi(N)    Hai(NW)
    //
    // We need to map `palace.earthlyBranch` to the visual grid index (0-11).
    // Let's create a visual order array based on Earthly Branches: 
    // Top Row: 巳(Si), 午(Wu), 未(Wei), 申(Shen)
    // Right Col: 酉(You), 戌(Xu)
    // Bot Row: 亥(Hai), 子(Zi), 丑(Chou), 寅(Yin)
    // Left Col: 卯(Mao), 辰(Chen)
    
    // Mapping Branch Char to a simple sort index to recreate the ring
    const branchOrder = ['巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑', '寅', '卯', '辰'];
    
    const getPalaceByBranch = (branch: string) => data.palaces.find(p => p.earthlyBranch === branch);

    return (
        <div className="w-full max-w-2xl mx-auto aspect-square p-1 md:p-4">
            <div className="grid grid-cols-4 grid-rows-4 gap-1 md:gap-2 h-full">
                {/* Row 1 */}
                <PalaceCard palace={getPalaceByBranch('巳')!} />
                <PalaceCard palace={getPalaceByBranch('午')!} />
                <PalaceCard palace={getPalaceByBranch('未')!} />
                <PalaceCard palace={getPalaceByBranch('申')!} />

                {/* Row 2 */}
                <PalaceCard palace={getPalaceByBranch('辰')!} />
                <PalaceCard palace={null as any} isCenter info={data} /> {/* Center takes 2x2 */}
                {/* Center is handled by col-span-2 row-span-2 logic in CSS grid, but React Grid needs empty placeholder or explicit placement */}
                {/* Actually, CSS Grid Area is cleaner, but flat grid with spans is easier for Tailwind without custom css */}
                <PalaceCard palace={getPalaceByBranch('酉')!} />

                {/* Row 3 */}
                <PalaceCard palace={getPalaceByBranch('卯')!} />
                {/* Center spans here too technically visually */}
                <PalaceCard palace={getPalaceByBranch('戌')!} />

                {/* Row 4 */}
                <PalaceCard palace={getPalaceByBranch('寅')!} />
                <PalaceCard palace={getPalaceByBranch('丑')!} />
                <PalaceCard palace={getPalaceByBranch('子')!} />
                <PalaceCard palace={getPalaceByBranch('亥')!} />
            </div>
        </div>
    );
};

export default AstrolabeView;
